"use client"

import { Popover } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBriefcase,
  faCheck,
  faChevronDown,
  faEllipsis,
  faFileLines,
  faGraduationCap,
  faMagnifyingGlass,
  faPlane,
  faSuitcaseRolling,
  faUsers,
  faXmark,
} from "@fortawesome/free-solid-svg-icons"
import { useState, type FC } from "react"
import { useTranslation } from "react-i18next"
import { useVisaCategoryOptions } from "@/features/visa/constants"
import { removeVietnameseChars } from "@/utils/string/removeVietnameseChars"

type SelectVisaCategoryProps = {
  value?: string
  onChange: (value?: string) => void
  className?: string
}

/** Field-shell cùng bộ khung với SelectDestination (icon trái, nhãn cố định phía trên, giá
 * trị/gợi ý phía dưới, chevron phải) — đúng theo ảnh mockup mục "Loại visa" trong box search. */
const SelectVisaCategory: FC<SelectVisaCategoryProps> = ({ value, onChange, className }) => {
  const { t } = useTranslation()
  const options = useVisaCategoryOptions()
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState("")
  const selected = options.find((option) => option.value === value)
  const categoryIcons = {
    TOURISM: { icon: faSuitcaseRolling, color: "#f43f5e" },
    BUSINESS: { icon: faBriefcase, color: "#3b82f6" },
    FAMILY: { icon: faUsers, color: "#8b5cf6" },
    STUDY: { icon: faGraduationCap, color: "#2563eb" },
    TRANSIT: { icon: faPlane, color: "#0ea5e9" },
    OTHER: { icon: faEllipsis, color: "#64748b" },
  } as const
  const normalizedSearch = removeVietnameseChars(searchText.trim()).toLowerCase()
  const filteredOptions = options.filter((option) =>
    removeVietnameseChars(option.label).toLowerCase().includes(normalizedSearch),
  )

  const handleSelect = (nextValue?: string) => {
    onChange(nextValue)
    setOpen(false)
    setSearchText("")
  }

  const content = (
    <div className="visa-category-popover">
      <div className="visa-category-popover__search">
        <FontAwesomeIcon icon={faMagnifyingGlass} aria-hidden />
        <input
          autoFocus
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t("visa.form_search.category_search_placeholder")}
        />
      </div>
      <div className="visa-category-popover__list">
        {filteredOptions.map((option) => (
          <button
            type="button"
            key={option.value}
            className={`visa-category-popover__item${value === option.value ? " is-selected" : ""}`}
            onClick={() => handleSelect(option.value)}
          >
            <span className="visa-category-popover__icon" style={{ color: categoryIcons[option.value].color }}>
              <FontAwesomeIcon icon={categoryIcons[option.value].icon} />
            </span>
            <span className="visa-category-popover__text">
              <strong>{option.label}</strong>
              <small>{t(`visa.form_search.category_${option.value.toLowerCase()}_hint`)}</small>
            </span>
            {value === option.value && <FontAwesomeIcon icon={faCheck} className="visa-category-popover__check" />}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearchText("")
      }}
      trigger="click"
      placement="bottomLeft"
      autoAdjustOverflow={false}
      content={content}
      overlayClassName="visa-destination-popover-overlay"
    >
      <div className={`visa__search-field${className ? ` ${className}` : ""}`}>
        <span className="visa__search-field-icon">
          <FontAwesomeIcon icon={faFileLines} />
        </span>
        <div className="visa__search-field-body">
          <span className="visa__search-field-label">{t("visa.form_search.category_label")}</span>
          <span className={`visa__search-field-value${selected ? "" : " visa__search-field-value--placeholder"}`}>
            {selected ? selected.label : t("visa.form_search.category_field_placeholder")}
          </span>
        </div>
        {selected ? (
          <FontAwesomeIcon
            icon={faXmark}
            className="visa__search-field-clear"
            onClick={(e) => {
              e.stopPropagation()
              handleSelect(undefined)
            }}
          />
        ) : (
          <FontAwesomeIcon icon={faChevronDown} className="visa__search-field-chevron" />
        )}
      </div>
    </Popover>
  )
}

export default SelectVisaCategory
